import axios from 'axios';

export const HTTP_CLIENT = axios.create({
  baseURL:
    window.location.hostname === 'localhost'
      ? 'http://localhost:3000'
      : 'https://finovva-backend.fly.dev',
  withCredentials: true,
});

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
};

function getDemoResponse(config, data) {
  return Promise.resolve({
    data,
    status: 200,
    statusText: 'OK',
    config,
    headers: {},
  });
}

function fnv1a(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

function interpolateDemoPrice(anchors, dateStr, seed) {
  const NOISE_AMP = 0.015; // ±1.5% daily noise around the trend line
  const target = new Date(dateStr).getTime();

  let lo = null;
  let hi = null;
  for (let i = 0; i < anchors.length; i++) {
    const t = new Date(anchors[i][0]).getTime();
    if (t <= target) lo = anchors[i];
    if (t >= target && hi === null) hi = anchors[i];
  }

  let base;
  if (!lo && !hi) return 1;
  else if (!lo) base = hi[1];
  else if (!hi) base = lo[1];
  else if (lo[0] === hi[0]) base = lo[1];
  else {
    const loT = new Date(lo[0]).getTime();
    const hiT = new Date(hi[0]).getTime();
    const frac = (target - loT) / (hiT - loT);
    base = lo[1] + frac * (hi[1] - lo[1]);
  }

  const h = fnv1a(seed);
  const noise = ((h % 10000) / 10000 - 0.5) * 2 * NOISE_AMP;
  return Math.max(base * (1 + noise), 0.001);
}

function buildDemoPriceMap(params, pricesByTicker) {
  const tickers = params?.tickers;
  const from = params?.from;
  const to = params?.to;
  if (!pricesByTicker || typeof pricesByTicker !== 'object') return {};
  const fromDate = from ? new Date(from) : new Date();
  const toDate = to ? new Date(to) : new Date();
  const oneDay = 24 * 60 * 60 * 1000;
  const tickerList = tickers && typeof tickers === 'string'
    ? tickers.split(',').map(t => t.trim()).filter(Boolean)
    : (Array.isArray(tickers) ? tickers : Object.keys(pricesByTicker));
  const priceMap = {};
  tickerList.forEach((ticker) => {
    const entry = pricesByTicker[ticker];
    const anchors = entry && typeof entry === 'object' && Array.isArray(entry.anchors)
      ? entry.anchors
      : null;
    const flatPrice = !anchors && Number.isFinite(Number(entry)) ? Number(entry) : null;

    const bars = [];
    let d = new Date(fromDate);
    while (d <= toDate) {
      if (d.getDay() !== 0 && d.getDay() !== 6) {
        const dateStr = d.toISOString().slice(0, 10);

        let open, close;
        if (anchors) {
          close = interpolateDemoPrice(anchors, dateStr, ticker + dateStr + 'c');
          open  = interpolateDemoPrice(anchors, dateStr, ticker + dateStr + 'o');
        } else {
          const p = flatPrice ?? 1;
          open = p;
          close = p;
        }

        const highNoise = ((fnv1a(ticker + dateStr + 'h') % 1000) / 1000) * 0.008;
        const lowNoise  = ((fnv1a(ticker + dateStr + 'l') % 1000) / 1000) * 0.008;
        const high = Math.max(open, close) * (1 + highNoise);
        const low  = Math.min(open, close) * (1 - lowNoise);
        const change = close - open;
        const changePercent = open > 0 ? (change / open) * 100 : 0;

        bars.push({
          date: dateStr,
          open:  +open.toFixed(4),
          high:  +high.toFixed(4),
          low:   +low.toFixed(4),
          close: +close.toFixed(4),
          adjClose: +close.toFixed(4),
          volume: 0,
          unadjustedVolume: 0,
          change: +change.toFixed(4),
          changePercent: +changePercent.toFixed(4),
          vwap: +((open + high + low + close) / 4).toFixed(4),
          label: dateStr,
          changeOverTime: 0, // filled in below
        });
      }
      d.setTime(d.getTime() + oneDay);
    }

    if (bars.length > 0) {
      const firstClose = bars[0].close;
      bars.forEach(bar => {
        bar.changeOverTime = firstClose > 0 ? (bar.close - firstClose) / firstClose : 0;
      });
    }

    priceMap[ticker] = bars;
  });
  return priceMap;
}

/**
 * In demo mode, intercepts GET requests and assigns an adapter that returns mock data.
 * Returns the same config (possibly with config.adapter set). Caller should use this
 * only when window.__FINOVVA_DEMO__ && window.__FINOVVA_DEMO_DATA__ are set.
 */
function applyDemoModeAdapter(config, demoData) {
  const url = config.url || '';
  const method = (config.method || 'get').toLowerCase();

  if (method !== 'get') return config;
  // Routes are matched by substring — keep entries specific enough to avoid
  // collisions. Per-route exclusions (e.g. !url.includes('bulk')) handle known
  // ambiguous cases. Verify no overlap when adding new entries.
  const routes = [
    {
      matches: () => url.includes('/bought-position') && !url.includes('/bought-position-check') && !url.includes('bulk') && !url.includes('ticker-delete'),
      payload: () => demoData.boughtPositions || [],
    },
    { matches: () => url.includes('/sold-position') && !url.includes('bulk'), payload: () => demoData.soldPositions || [] },
    {
      matches: () => url.includes('/current-position') && !url.includes('recalculate') && !url.includes('total-value') && !url.includes('bulk'),
      payload: () => demoData.currentPositions || [],
    },
    { matches: () => url.includes('/cash-position') && !url.includes('bulk'), payload: () => demoData.cashPositions || [] },
    {
      matches: () => url.includes('/get-dividends'),
      payload: () => {
        const dividendMap = demoData.dividendMap || {};
        const tickers = config.params?.tickers;
        const map = tickers && Array.isArray(tickers)
          ? Object.fromEntries(tickers.filter(t => dividendMap[t]).map(t => [t, dividendMap[t]]))
          : dividendMap;
        return { dividendMap: map };
      }
    },
    { matches: () => url.includes('/get-prices'), payload: () => ({ priceMap: buildDemoPriceMap(config.params, demoData.pricesByTicker) }) },
    {
      matches: () => url.includes('/get-profiles'),
      payload: () => {
        const profileMap = demoData.profileMap || {};
        const tickers = config.params?.tickers;
        const map = tickers && Array.isArray(tickers)
          ? Object.fromEntries(tickers.filter(t => profileMap[t]).map(t => [t, profileMap[t]]))
          : profileMap;
        return { profileMap: map };
      }
    },
    { matches: () => url.includes('/dividend-calendar'), payload: () => demoData.dividendCalendar || [] },
    {
      matches: () => url.includes('/earnings/'),
      payload: () => {
        const earningsMatch = url.match(/\/earnings\/([^/?#]+)/i);
        const symbol = earningsMatch?.[1]?.toUpperCase() || '';
        const earningsMap = demoData.earningsByTicker || {};
        return symbol && earningsMap[symbol] ? earningsMap[symbol] : { symbol: symbol || 'UNKNOWN', earnings: [] };
      }
    },
    {
      matches: () => url.includes('/analyst-grades/'),
      payload: () => {
        const analystMatch = url.match(/\/analyst-grades\/([^/?#]+)/i);
        const symbol = analystMatch?.[1]?.toUpperCase() || '';
        const gradesMap = demoData.analystGradesByTicker || {};
        return symbol && gradesMap[symbol]
          ? gradesMap[symbol]
          : {
              symbol: symbol || 'UNKNOWN',
              date: '2026-04-01',
              analystRatingsStrongBuy: 0,
              analystRatingsBuy: 0,
              analystRatingsHold: 0,
              analystRatingsSell: 0,
              analystRatingsStrongSell: 0,
            };
      }
    },
    {
      matches: () => url.includes('/price-targets/'),
      payload: () => {
        const priceTargetMatch = url.match(/\/price-targets\/([^/?#]+)/i);
        const symbolPt = priceTargetMatch?.[1]?.toUpperCase() || '';
        const priceTargetsMap = demoData.priceTargetsByTicker || {};
        return symbolPt && priceTargetsMap[symbolPt] ? priceTargetsMap[symbolPt] : [];
      }
    },
    {
      matches: () => url.includes('/similar-stocks/'),
      payload: () => {
        const similarMatch = url.match(/\/similar-stocks\/([^/?#]+)/i);
        const symbol = similarMatch?.[1]?.toUpperCase() || '';
        const similarMap = demoData.similarStocksByTicker || {};
        return symbol && similarMap[symbol] ? similarMap[symbol] : { similarAssets: [], assetType: 'stock' };
      }
    },
    {
      matches: () => url.includes('/income-statements/'),
      payload: () => {
        const incomeMatch = url.match(/\/income-statements\/([^/?#]+)/i);
        const symbolInc = incomeMatch?.[1]?.toUpperCase() || '';
        const incomeMap = demoData.incomeStatementsByTicker || {};
        return symbolInc && incomeMap[symbolInc]
          ? { symbol: incomeMap[symbolInc].symbol || symbolInc, incomeStatements: incomeMap[symbolInc].incomeStatements || [] }
          : { incomeStatements: [] };
      }
    },
    {
      matches: () => url.includes('/balance-sheet-statements/'),
      payload: () => {
        const balanceMatch = url.match(/\/balance-sheet-statements\/([^/?#]+)/i);
        const symbolBal = balanceMatch?.[1]?.toUpperCase() || '';
        const balanceMap = demoData.balanceSheetStatementsByTicker || {};
        return symbolBal && balanceMap[symbolBal]
          ? { symbol: balanceMap[symbolBal].symbol || symbolBal, balanceSheetStatements: balanceMap[symbolBal].balanceSheetStatements || [] }
          : { balanceSheetStatements: [] };
      }
    },
    {
      matches: () => url.includes('/cash-flow-statements/'),
      payload: () => {
        const cashFlowMatch = url.match(/\/cash-flow-statements\/([^/?#]+)/i);
        const symbolCf = cashFlowMatch?.[1]?.toUpperCase() || '';
        const cashFlowMap = demoData.cashFlowStatementsByTicker || {};
        return symbolCf && cashFlowMap[symbolCf]
          ? { symbol: cashFlowMap[symbolCf].symbol || symbolCf, cashFlowStatements: cashFlowMap[symbolCf].cashFlowStatements || [] }
          : { cashFlowStatements: [] };
      }
    },
    {
      matches: () => url.includes('/financial-health-score/'),
      payload: () => {
        const healthMatch = url.match(/\/financial-health-score\/([^/?#]+)/i);
        const symbolHealth = healthMatch?.[1]?.toUpperCase() || '';
        const healthMap = demoData.financialHealthByTicker || {};
        return symbolHealth && healthMap[symbolHealth] ? healthMap[symbolHealth] : { healthScore: 0 };
      }
    },
    {
      matches: () => url.includes('/financial-ratios-ttm/'),
      payload: () => {
        const ratioTtmMatch = url.match(/\/financial-ratios-ttm\/([^/?#]+)/i);
        const symbolTtm = ratioTtmMatch?.[1]?.toUpperCase() || 'UNKNOWN';
        const ratiosTtmMap = demoData.financialRatiosTtmByTicker || {};
        return symbolTtm && ratiosTtmMap[symbolTtm] ? ratiosTtmMap[symbolTtm] : { symbol: symbolTtm, ratiosTTM: [] };
      }
    },
    {
      matches: () => url.includes('/financial-ratios/'),
      payload: () => {
        const ratioMatch = url.match(/\/financial-ratios\/([^/?#]+)/i);
        const symbol = ratioMatch?.[1]?.toUpperCase() || 'UNKNOWN';
        const ratiosMap = demoData.financialRatiosByTicker || {};
        return symbol && ratiosMap[symbol] ? ratiosMap[symbol] : { symbol, ratios: [] };
      }
    },
    { matches: () => url.includes('/portfolio-information'), payload: () => ({ portfolioInfo: demoData.portfolioInfo }) },
  ];

  for (const route of routes) {
    if (!route.matches()) continue;
    config.adapter = () => getDemoResponse(config, route.payload());
    return config;
  }

  // No stub matched — request will hit the real backend in demo mode.
  // If this produces empty/broken UI, add a matching entry to the routes array above.
  console.warn('[Demo mode] No stub for GET', url, '— falling through to real backend');
  return config;
}

export const setupAxios = () => {
  HTTP_CLIENT.interceptors.request.use(
    (config) => {
      // Always attach auth token first so every request (including non-GETs in demo mode) has it
      const token = getCookie('jwtToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Demo mode: intercept GETs and return mock portfolio data where a stub exists.
      // Any URL without a matching stub falls through to the real backend unchanged.
      if (typeof window !== 'undefined' && window.__FINOVVA_DEMO__ && window.__FINOVVA_DEMO_DATA__) {
        return applyDemoModeAdapter(config, window.__FINOVVA_DEMO_DATA__);
      }

      return config;
    },
    (err) => {
      return Promise.reject(err);
    }
  );

  // Refresh token after 1h
  HTTP_CLIENT.interceptors.response.use(
    response => response,
    async (error) => {
      const originalRequest = error.config;
      const backendUrl = window.location.hostname === 'localhost'
          ? 'http://localhost:3000'
          : 'https://finovva-backend.fly.dev';

      if (error.response?.status === 403 && error.response?.data === 'Forbidden' && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const { data } = await axios.post(`${backendUrl}/refresh-auth-token`, {}, { withCredentials: true });
          document.cookie = `jwtToken=${data.accessToken}; path=/;`; // Update the cookie
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          return HTTP_CLIENT(originalRequest);
        } catch (err) {
          console.error('Refresh token request failed', err);
          window.location.href = '/signin';
        }
      }
      return Promise.reject(error);
    }
  );
};
